import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, X, Upload, Image as ImageIcon, AlertTriangle } from 'lucide-react';
import getDirectImageUrl from '../../../../utils/getDirectImageUrl';

/**
 * MediaSection component with updated image validation
 * - Enforces 1MB size limit for all images
 */
const MediaSection = ({ formData, setFormData, errors }) => {
  const [newTag, setNewTag] = useState('');
  const [localMainImage, setLocalMainImage] = useState(null);
  const [localAdditionalImages, setLocalAdditionalImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageError, setImageError] = useState(null);
  
  // Use refs to track object URLs
  const objectURLsRef = useRef([]);
  
  // Default image
  const defaultImage = '/images/default-event-image.jpg';
  
  // File type validation
  const validImageTypes = [
    'image/jpeg', 
    'image/jpg', 
    'image/png', 
    'image/gif', 
    'image/webp'
  ];
  
  // Max file size (1MB) - updated per requirements
  const MAX_FILE_SIZE = 1 * 1024 * 1024;

  // Initialize preview state
  useEffect(() => {
    if (formData.mainPicture && !localMainImage) {
      setLocalMainImage({
        preview: formData.mainPicture,
        isExisting: true
      });
    }
    
    // Initialize local additional images from formData.pictures
    if (formData.pictures && formData.pictures.length > 0 && localAdditionalImages.length === 0) {
      const existingImages = formData.pictures.map(url => ({
        preview: url,
        isExisting: true
      }));
      setLocalAdditionalImages(existingImages);
    }
  }, [formData.mainPicture, formData.pictures, localMainImage, localAdditionalImages.length]);

  // Add object URL to tracking list
  const trackObjectURL = (url) => {
    if (url && url.startsWith('blob:')) {
      objectURLsRef.current.push(url);
    }
  };

  // Clean up all object URLs
  const cleanupObjectURLs = () => {
    objectURLsRef.current.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        console.warn('Failed to revoke object URL:', e);
      }
    });
    objectURLsRef.current = [];
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupObjectURLs();
    };
  }, []);

  // Main image selection handler with size validation
  const handleMainImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImageError(null);
    console.log('Selected main image file type:', file.type, 'file size:', (file.size / 1024).toFixed(2) + 'KB');
    
    // Validate file type
    if (!validImageTypes.includes(file.type)) {
      setImageError('Please select a valid image format: JPG, PNG, GIF, or WebP');
      return;
    }
    
    // Validate file size - strict 1MB limit
    if (file.size > MAX_FILE_SIZE) {
      setImageError(`Image size exceeds 1MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB). Please use a smaller image.`);
      return;
    }
    
    try {
      // Create temporary preview
      const objectUrl = URL.createObjectURL(file);
      trackObjectURL(objectUrl);
      
      // Set local image preview state
      setLocalMainImage({
        file: file,
        preview: objectUrl,
        isExisting: false,
        isUploading: false
      });
      
      // Save file reference in form data for later upload
      setFormData(prev => ({
        ...prev,
        mainPictureFile: file,
        // Keep original URL if in edit mode, otherwise set to empty string
        mainPicture: prev.mainPicture || ''
      }));
      
    } catch (error) {
      console.error('Error processing main image:', error);
      setImageError('Image processing failed, please try again');
      
      // Reset local state on failure
      setLocalMainImage(null);
    }
  };

  // Additional image selection handler with size validation
  const handleAdditionalImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImageError(null);
    console.log('Selected additional image file type:', file.type, 'file size:', (file.size / 1024).toFixed(2) + 'KB');
    
    // Validate file type
    if (!validImageTypes.includes(file.type)) {
      setImageError('Please select a valid image format: JPG, PNG, GIF, or WebP');
      return;
    }
    
    // Validate file size - strict 1MB limit
    if (file.size > MAX_FILE_SIZE) {
      setImageError(`Image size exceeds 1MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB). Please use a smaller image.`);
      return;
    }
    
    try {
      // Create temporary preview
      const objectUrl = URL.createObjectURL(file);
      trackObjectURL(objectUrl);
      
      // Add to local state for preview
      setLocalAdditionalImages(prev => [
        ...prev, 
        {
          file: file,
          preview: objectUrl,
          isExisting: false,
          isUploading: false
        }
      ]);
      
      // Save file reference to form data for later upload
      setFormData(prev => ({
        ...prev,
        additionalPictureFiles: [...(prev.additionalPictureFiles || []), file]
      }));
      
    } catch (error) {
      console.error('Error processing additional image:', error);
      setImageError('Image processing failed, please try again');
    }
  };

  // Remove main picture
  const handleRemoveMainPicture = () => {
    console.log('Removing main picture');
    
    if (localMainImage && !localMainImage.isExisting && localMainImage.preview.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(localMainImage.preview);
        objectURLsRef.current = objectURLsRef.current.filter(u => u !== localMainImage.preview);
      } catch (e) {
        console.warn('Failed to revoke object URL:', e);
      }
    }
    
    setLocalMainImage(null);
    
    setFormData(prev => {
      const newData = { ...prev };
      // If there was a previous main picture, mark it for removal on the server
      if (prev.mainPicture) {
        newData.mainPictureToRemove = prev.mainPicture;
        newData.mainPicture = '';
      }
      
      return newData;
    });
  };

  // Remove additional picture
  const handleRemoveAdditionalPicture = (index) => {
    const imageToRemove = localAdditionalImages[index];
    
    // If it's a blob URL, revoke it
    if (imageToRemove && !imageToRemove.isExisting && imageToRemove.preview.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(imageToRemove.preview);
        objectURLsRef.current = objectURLsRef.current.filter(u => u !== imageToRemove.preview);
      } catch (e) {
        console.warn('Failed to revoke object URL:', e);
      }
    }
    
    // Remove from local state
    setLocalAdditionalImages(prev => prev.filter((_, i) => i !== index));
    
    // Remove from form data
    setFormData(prev => {
      const pictures = [...(prev.pictures || [])];
      pictures.splice(index, 1);
      
      return {
        ...prev,
        pictures
      };
    });
  };

  // Add tag
  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  // Remove tag
  const handleRemoveTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(t => t !== tag)
    }));
  };

  // Handle key press events
  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  // Handle image load errors
  const handleImageError = (e) => {
    console.warn('Image load failed, using default image');
    e.target.src = defaultImage;
    e.target.onerror = null; // Prevent infinite loop
  };

  return (
    <div className="space-y-6">
      {/* Main image upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Main Picture *
        </label>
        
        {/* Error message */}
        {imageError && (
          <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-3">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <p className="ml-2 text-sm text-red-700">{imageError}</p>
            </div>
          </div>
        )}
        
        {isUploading && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center mb-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
              <div 
                className="bg-[#FFD200] h-2.5 rounded-full" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600">Processing image...</p>
          </div>
        )}
        
        {!isUploading && (localMainImage || formData.mainPicture) ? (
          <div className="relative mb-4">
            <div className="rounded-lg overflow-hidden w-full h-40 bg-gray-100">
              <img
                src={localMainImage ? localMainImage.preview : getDirectImageUrl(formData.mainPicture)}
                alt="Main event image"
                className="w-full h-full object-cover"
                onError={handleImageError}
              />
              {localMainImage && localMainImage.isUploading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleRemoveMainPicture}
              className="absolute top-2 right-2 p-1.5 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
              disabled={localMainImage && localMainImage.isUploading}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center mb-4">
            <Upload className="h-8 w-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-500 mb-2">Upload main event image</p>
            <p className="text-xs text-gray-400 mb-1">Supports JPG, PNG, GIF, WebP formats</p>
            <p className="text-xs font-bold text-red-500 mb-4">Maximum size: 1MB</p>
            <label className="inline-flex cursor-pointer items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-[#2C2C2C] bg-[#FFD200] hover:bg-[#FFE566] transition-colors">
              Select Image
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                className="sr-only"
                onChange={handleMainImageSelect}
                disabled={isUploading}
              />
            </label>
          </div>
        )}
      </div>

      {/* Additional images */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Images
        </label>

        {/* Image gallery */}
        {localAdditionalImages.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {localAdditionalImages.map((image, index) => (
              <div key={`additional-${index}`} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={image.preview}
                  alt={`Event photo ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={handleImageError}
                />
                {image.isUploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveAdditionalPicture(index)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
                  disabled={image.isUploading}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Upload button */}
        <div className="flex items-center justify-center">
          {isUploading ? (
            <div className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-gray-100 text-gray-500">
              <div className="animate-spin mr-2 h-4 w-4 border-b-2 border-gray-600"></div>
              Uploading...
            </div>
          ) : (
            <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 cursor-pointer">
              <Upload className="mr-2 h-4 w-4" />
              Add Photo (Max 1MB)
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                className="sr-only"
                onChange={handleAdditionalImageSelect}
                disabled={isUploading}
              />
            </label>
          )}
        </div>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>
        <div className="flex space-x-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyPress={(e) => handleKeyPress(e, handleAddTag)}
            placeholder="Enter tag"
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#FFD200] focus:ring-[#FFD200] sm:text-sm"
          />
          <button
            type="button"
            onClick={handleAddTag}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-[#2C2C2C] bg-[#FFD200] hover:bg-[#FFE566] transition-colors"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {(formData.tags || []).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Video URL with YouTube validation notice */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Video URL
        </label>
        <input
          type="url"
          name="video"
          value={formData.video || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, video: e.target.value }))}
          className={`w-full rounded-md ${errors.video ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-[#FFD200] focus:ring-[#FFD200]'} shadow-sm sm:text-sm`}
          placeholder="https://youtube.com/... (YouTube links only)"
        />
        {errors.video && (
          <p className="mt-1 text-sm text-red-600">{errors.video}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          <span className="font-bold">Only YouTube links are allowed.</span> Users will be directed to the video link first if available.
        </p>
      </div>
    </div>
  );
};

export default MediaSection;